import { yupResolver } from '@hookform/resolvers/yup';
import { Button, ButtonLevel, ButtonSize, Checkbox } from '@vizit/atoms';
import { FormCheckboxGroup, FormDialog, FormTextField } from '@vizit/forms';
import { Edit03Icon, PlusIcon } from '@vizit/icons';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

type OrganizationFormSchema = {
  organizationName: string;
  accountType?: string;
  categories: string[];
  retailers: string[];
};

interface OrganizationDialogProps {
  /**
   * The id of the organization
   */
  id: string;
  /**
   * Default values for the form
   */
  defaultValues?: Partial<OrganizationFormSchema>;
  /**
   * Whether the dialog is open
   */
  isOpen?: boolean;
  /**
   * ICP product categories
   */
  icpProductCategories?: Record<string, string>;
  /**
   * Retailer regions available to associate with the organization
   */
  retailerRegions?: Record<string, string>;
  /**
   * Retailer region ids that should be rendered as disabled (pre-selected, managed by backend)
   */
  disabledRetailers?: string[];
  /**
   * Variant of the dialog
   */
  variant: 'create' | 'edit';
  /**
   * Whether to show the icon
   */
  withIcon?: boolean;
  /**
   * Whether the dialog is in a table
   */
  inTable?: boolean;
  /**
   * Whether the dialog is disabled
   */
  isDisabled?: boolean;
  /**
   * Callback function to handle the organization api call
   */
  onSubmit: (data: OrganizationFormSchema) => Promise<void> | void;
  /**
   * Callback fired when the dialog open state changes
   */
  onOpenChange?: (isOpen: boolean) => void;
  /**
   * Ref object for the dialog
   */
  ref?: React.RefObject<HTMLButtonElement>;
}

function OrganizationDialog(props: OrganizationDialogProps) {
  const {
    defaultValues: defaultValuesProp,
    id,
    isDisabled,
    isOpen: isOpenProp,
    icpProductCategories,
    retailerRegions,
    disabledRetailers,
    variant = 'create',
    ref,
    withIcon = false,
    inTable = false,
    onSubmit,
    onOpenChange,
  } = props;

  const [isOpen, setIsOpen] = React.useState(isOpenProp || false);

  const defaultValues = React.useMemo(
    () => ({
      organizationName: defaultValuesProp?.organizationName ?? '',
      accountType: defaultValuesProp?.accountType ?? '',
      categories: defaultValuesProp?.categories ?? [],
      retailers: [...new Set([...(disabledRetailers ?? []), ...(defaultValuesProp?.retailers ?? [])])],
    }),
    [defaultValuesProp, disabledRetailers],
  );

  const categoryKeys = React.useMemo(() => Object.keys(icpProductCategories || {}), [icpProductCategories]);

  const formSchema = React.useMemo(
    () =>
      yup.object().shape({
        organizationName: yup.string().required('Name is required'),
        categories: yup
          .array()
          .of(yup.string().defined().oneOf(categoryKeys))
          .min(1, 'At least one category is required')
          .required('ICP product categories are required'),
        retailers: yup.array().of(yup.string().defined()).defined(),
      }),
    [categoryKeys],
  );

  const resolver = React.useMemo(() => yupResolver(formSchema), [formSchema]);

  const methods = useForm<OrganizationFormSchema>({
    resolver,
    // `values` (instead of `defaultValues`) makes RHF reactively sync the form
    // when `defaultValues` changes — fixes the race condition where per-org
    // retailer data loads *after* the dialog opens and `reset()` was already called.
    values: defaultValues,
    mode: 'all',
    delayError: 650,
  });

  const { control, formState, handleSubmit, reset } = methods;

  const handleOnSubmit = async (data: OrganizationFormSchema) => {
    await onSubmit(data);

    setIsOpen(false);

    reset(
      variant === 'create'
        ? {
            organizationName: '',
            accountType: '',
            categories: [],
            retailers: [],
          }
        : data,
    );
  };

  const handleOnResetForm = () => {
    reset(defaultValues);
  };

  const handleOnOpenChange = (open: boolean) => {
    setIsOpen(open);
    onOpenChange?.(open);
  };

  const buttonSize = React.useMemo(() => {
    if (variant === 'edit' && inTable) {
      return ButtonSize.Sm;
    }
    if (variant === 'edit' && !inTable) {
      return ButtonSize.Md;
    }
    return ButtonSize.Xl;
  }, [variant, inTable]);

  return (
    <FormDialog<OrganizationFormSchema>
      formName={`organization-${id}`}
      formMethods={methods}
      isOpen={isOpen}
      onSubmit={handleSubmit(handleOnSubmit)}
      onResetForm={handleOnResetForm}
      onOpenChange={handleOnOpenChange}
    >
      <FormDialog.Trigger>
        <Button
          className={variant === 'edit' ? 'text-sm font-semibold' : undefined}
          size={buttonSize}
          iconStart={
            variant === 'edit' && withIcon ? (
              <Edit03Icon className="text-gray-utility-400" size={20} />
            ) : (
              <PlusIcon size={20} />
            )
          }
          ref={ref}
          level={variant === 'edit' ? ButtonLevel.Secondary : ButtonLevel.Primary}
          isDisabled={isDisabled}
        >
          {variant === 'create' ? 'Create Organization' : 'Edit'}
        </Button>
      </FormDialog.Trigger>
      <FormDialog.Title>{variant === 'create' ? 'Create Organization' : 'Edit Organization'}</FormDialog.Title>
      <FormDialog.Subtitle>
        {variant === 'create'
          ? 'Set up a new organization to start scoring products.'
          : 'Update organization details to keep account current.'}
      </FormDialog.Subtitle>
      <FormDialog.FormFields>
        <FormTextField
          control={control}
          isDisabled={formState.isSubmitting || formState.isSubmitSuccessful}
          name="organizationName"
          label="Name"
          isRequired
        />
        <FormTextField
          control={control}
          isDisabled={formState.isSubmitting || formState.isSubmitSuccessful}
          name="accountType"
          label="Account type"
        />
        {retailerRegions && (
          <FormCheckboxGroup
            control={control}
            isDisabled={formState.isSubmitting || formState.isSubmitSuccessful}
            name="retailers"
            label="Retailers"
          >
            {Object.entries(retailerRegions).map(([id, display_name]) => (
              <Checkbox key={id} value={id} isDisabled={disabledRetailers?.includes(id)}>
                {display_name}
              </Checkbox>
            ))}
          </FormCheckboxGroup>
        )}
        {icpProductCategories && (
          <FormCheckboxGroup
            control={control}
            isDisabled={formState.isSubmitting || formState.isSubmitSuccessful}
            name="categories"
            label="ICP product categories"
            isRequired
            hasSelectAll
          >
            {Object.entries(icpProductCategories).map(([id, category_name]) => (
              <Checkbox key={id} value={id}>
                {category_name}
              </Checkbox>
            ))}
          </FormCheckboxGroup>
        )}
      </FormDialog.FormFields>
      <FormDialog.SubmitButton>{variant === 'create' ? 'Create' : 'Update'}</FormDialog.SubmitButton>
    </FormDialog>
  );
}

export { OrganizationDialog };
export type { OrganizationDialogProps, OrganizationFormSchema };
